import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Analytics, Dthc, GlobalNotifications, Intent } from '../../Modules';
import ContactEditDesktop from './ContactEditDesktop';
import ContactShow from './ContactShow';
import NewContactDesktop from './NewContactDesktop';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import { commitMutation, graphql } from 'react-relay';
import { ConnectionHandler } from 'relay-runtime';
import { Button } from 'semantic-ui-react';

export default class Contacts extends React.Component {
  static propTypes = {
    event: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    refetchEvent: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      avatarCache: {},
      localContacts: _.cloneDeep(this.props.event.user_contacts.edges),
      editorOpen: false,
      newContact: {
        node: {
          first_name: '',
          last_name: '',
          company_name: '',
          user_contact_phones: { edges: [] },
          user_contact_addresses: { edges: [] },
          user_contact_emails: { edges: [] }
        },
        role: ''
      }
    };
    this.updateAvatarCache = this.updateAvatarCache.bind(this);
    this.contactChangeHandler = this.contactChangeHandler.bind(this);
    this.newContactChangeHandler = this.newContactChangeHandler.bind(this);
    this.updateContact = this.updateContact.bind(this);
    this.deleteContact = this.deleteContact.bind(this);
    this.deleteUserContactEmail = this.deleteUserContactEmail.bind(this);
    this.deleteUserContactPhone = this.deleteUserContactPhone.bind(this);
    this.deleteUserContactAddress = this.deleteUserContactAddress.bind(this);
    this.deleteNewUserContactEmail = this.deleteNewUserContactEmail.bind(this);
    this.deleteNewUserContactPhone = this.deleteNewUserContactPhone.bind(this);
    this.deleteNewUserContactAddress = this.deleteNewUserContactAddress.bind(
      this
    );
    this.addContact = this.addContact.bind(this);
    this.resetContact = this.resetContact.bind(this);
    this.resetNewContact = this.resetNewContact.bind(this);
    this.createContact = this.createContact.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const obj = _.cloneDeep(this.state);
    obj.localContacts = _.cloneDeep(nextProps.event.user_contacts.edges);
    this.setState({ ...obj });
  }

  shouldComponentUpdate() {
    return (
      this.props.event.user_contacts.edges.length ===
      this.state.localContacts.length
    );
  }

  contactChangeHandler(contact, idx) {
    const obj = _.cloneDeep(this.state);
    obj.localContacts[idx] = contact;
    this.setState(obj);
  }

  newContactChangeHandler(contact) {
    const obj = _.cloneDeep(this.state);
    obj.newContact = contact;
    this.setState({ ...obj });
  }

  updateAvatarCache(hash, avatarLoaded) {
    const newAvatar = {};
    newAvatar[hash] = avatarLoaded;
    this.setState({
      avatarCache: Object.assign(this.state.avatarCache, newAvatar)
    });
  }

  resetContact(idx) {
    const obj = _.cloneDeep(this.state);
    obj.localContacts[idx] = _.cloneDeep(
      this.props.event.user_contacts.edges[idx]
    );
    this.setState(obj);
  }

  resetNewContact() {
    this.setState(prevState => {
      const obj = _.cloneDeep(prevState);
      obj.newContact = {
        node: {
          first_name: '',
          last_name: '',
          company_name: '',
          user_contact_phones: { edges: [] },
          user_contact_addresses: { edges: [] },
          user_contact_emails: { edges: [] }
        },
        role: ''
      };
      obj.editorOpen = false;
      return obj;
    });
  }

  updateContact(idx, successCb, failureCb) {
    const contact = nullifyEmptyStrings(this.state.localContacts[idx].node);
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update contact',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated contact',
        intent: Intent.SUCCESS
      });
      this.props.refetchEvent();
      Analytics.trackEvent('updated-contact');
    };

    const mutation = graphql`
      mutation Contacts_UpdateUserContactMutation(
        $input: UpdateUserContactInput!
      ) {
        updateUserContact(input: $input) {
          user_contact {
            id
            first_name
            last_name
            company_name
            user_contact_addresses {
              edges {
                node {
                  id
                  label
                  line1
                  line2
                  city
                  province
                  postal_code
                  country
                }
              }
            }
            user_contact_emails {
              edges {
                node {
                  id
                  label
                  address
                }
              }
            }
            user_contact_phones {
              edges {
                node {
                  id
                  label
                  number
                }
              }
            }
          }
        }
      }
    `;
    // eslint-disable-next-line camelcase
    const user_contact = {
      id: contact.id,
      first_name: contact.first_name,
      last_name: contact.last_name,
      company_name: contact.company_name,
      phones: contact.user_contact_phones.edges.map(userContactPhone => {
        const number = userContactPhone.node.number;
        const reformattedNumber = number ? number.replace(/\D/g, '') : null;
        return {
          id: userContactPhone.node.id,
          label: userContactPhone.node.label,
          number: reformattedNumber === '' ? null : reformattedNumber
        };
      }),
      emails: contact.user_contact_emails.edges.map(userContactEmail => ({
        id: userContactEmail.node.id,
        label: userContactEmail.node.label,
        address: userContactEmail.node.address
      })),
      addresses: contact.user_contact_addresses.edges.map(
        userContactAddress => ({
          id: userContactAddress.node.id,
          label: userContactAddress.node.label,
          line1: userContactAddress.node.line1,
          line2: userContactAddress.node.line2,
          city: userContactAddress.node.city,
          province: userContactAddress.node.province,
          postal_code: userContactAddress.node.postal_code,
          country: userContactAddress.node.country
        })
      ),
      roles: [{ id: this.state.localContacts[idx].id, role: this.state.localContacts[idx].role }],
      delete_phones_with_ids: contact.deletePhonesWithIds,
      delete_emails_with_ids: contact.deleteEmailsWithIds,
      delete_addresses_with_ids: contact.deleteAddressesWithIds
    };
    const optimisticResponse = {
      updateUserContact: { user_contact: contact }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          // eslint-disable-next-line camelcase
          ...user_contact
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  deleteContact(idx, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete contact',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully deleted contact',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('deleted-contact');
    };

    const mutation = graphql`
      mutation Contacts_deleteUserContactMutation(
        $input: DeleteUserContactInput!
      ) {
        deleteUserContact(input: $input) {
          deletedUserContactId
        }
      }
    `;

    const environment = this.props.relay.environment;
    const parentID = this.props.event.id;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID,
        pathToConnection: ['event', 'user_contact'],
        deletedIDFieldName: 'deletedUserContactId',
        connectionKeys: [
          {
            key: 'EventSummaryContainer_user_contacts',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.event.user_contacts.edges[idx].node.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  deleteUserContactEmail(contactIdx, emailIdx) {
    const obj = _.cloneDeep(this.state);
    const existingContactEmailId = this.state.localContacts[contactIdx].node
      .user_contact_emails.edges[emailIdx].node.id;
    const contactNode = obj.localContacts[contactIdx].node;
    if (existingContactEmailId) {
      contactNode.deleteEmailsWithIds = contactNode.deleteEmailsWithIds
        ? contactNode.deleteEmailsWithIds.push(existingContactEmailId)
        : [existingContactEmailId];
    }
    obj.localContacts[contactIdx].node.user_contact_emails.edges.splice(emailIdx, 1);
    this.setState(obj);
  }

  deleteUserContactPhone(contactIdx, phoneIdx) {
    const obj = _.cloneDeep(this.state);
    const existingContactPhoneId = this.state.localContacts[contactIdx].node
      .user_contact_phones.edges[phoneIdx].node.id;
    const contactNode = obj.localContacts[contactIdx].node;
    if (existingContactPhoneId) {
      contactNode.deletePhonesWithIds = contactNode.deletePhonesWithIds
        ? contactNode.deletePhonesWithIds.push(existingContactPhoneId)
        : [existingContactPhoneId];
    }
    obj.localContacts[contactIdx].node.user_contact_phones.edges.splice(phoneIdx, 1);
    this.setState(obj);
  }

  deleteUserContactAddress(contactIdx, addressIdx) {
    const obj = _.cloneDeep(this.state);
    const existingContactAddressId = this.state.localContacts[contactIdx].node
      .user_contact_addresses.edges[addressIdx].node.id;
    const contactNode = obj.localContacts[contactIdx].node;
    if (existingContactAddressId) {
      contactNode.deleteAddressesWithIds = contactNode.deleteAddressesWithIds
        ? contactNode.deleteAddressesWithIds.push(existingContactAddressId)
        : [existingContactAddressId];
    }
    obj.localContacts[contactIdx].node.user_contact_addresses.edges.splice(addressIdx, 1);
    this.setState(obj);
  }

  addContact() {
    this.setState({ editorOpen: true });
  }

  createContact(idx, successCb, failureCb) {
    const contact = _.cloneDeep(this.state.newContact.node);
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to create contact',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully created contact',
        intent: Intent.SUCCESS
      });
      this.resetNewContact();
      this.props.refetchEvent();
      Analytics.trackEvent('created-contact');
    };

    const mutation = graphql`
      mutation Contacts_CreateUserContactMutation(
        $input: CreateUserContactInput!
      ) {
        createUserContact(input: $input) {
          user_contact {
            id
            first_name
            last_name
            company_name
            user_contact_addresses {
              edges {
                node {
                  id
                  label
                  line1
                  line2
                  city
                  province
                  postal_code
                  country
                }
              }
            }
            user_contact_emails {
              edges {
                node {
                  id
                  label
                  address
                }
              }
            }
            user_contact_phones {
              edges {
                node {
                  id
                  label
                  number
                }
              }
            }
          }
          new_user_contact_edge {
            node {
              id
              first_name
              last_name
              company_name
              user_contact_addresses {
                edges {
                  node {
                    id
                    label
                    line1
                    line2
                    city
                    province
                    postal_code
                    country
                  }
                }
              }
              user_contact_emails {
                edges {
                  node {
                    id
                    label
                    address
                  }
                }
              }
              user_contact_phones {
                edges {
                  node {
                    id
                    label
                    number
                  }
                }
              }
            }
          }
        }
      }
    `;
    // eslint-disable-next-line camelcase
    const user_contact = {
      user_id: this.props.viewer.id,
      first_name: contact.first_name,
      last_name: contact.last_name,
      company_name: contact.company_name,
      phones: contact.user_contact_phones.edges.map(userContactPhone => {
        const number = userContactPhone.node.number;
        const reformattedNumber = number ? number.replace(/\D/g, '') : null;
        return {
          label: userContactPhone.node.label,
          number: reformattedNumber === '' ? null : reformattedNumber
        };
      }),
      emails: contact.user_contact_emails.edges.map(userContactEmail => ({
        label: userContactEmail.node.label,
        address: userContactEmail.node.address
      })),
      addresses: contact.user_contact_addresses.edges.map(
        userContactAddress => ({
          label: userContactAddress.node.label,
          line1: userContactAddress.node.line1,
          line2: userContactAddress.node.line2,
          city: userContactAddress.node.city,
          province: userContactAddress.node.province,
          postal_code: userContactAddress.node.postal_code,
          country: userContactAddress.node.country
        })
      ),
      events: [
        { role: this.state.newContact.role, event_id: this.props.event.id }
      ]
    };

    // const optimisticResponse = {
    //   createUserContact: { user_contact: contact, new_user_contact_edge: { node: contact } }
    // };

    const environment = this.props.relay.environment;
    // const parentID = this.props.event.id;

    // const configs = [
    //   {
    //     type: 'RANGE_ADD',
    //     parentID,
    //     edgeName: 'new_user_contact_edge',
    //     connectionInfo: [
    //       {
    //         key: 'EventSummaryContainer_user_contacts',
    //         rangeBehavior: 'append'
    //       }
    //     ]
    //   }
    // ];

    const updater = store => {
      const eventProxy = store.get(this.props.event.id);
      const payload = store.getRootField('createUserContact');
      const newNode = payload.getLinkedRecord('user_contact');
      const conn = ConnectionHandler.getConnection(
        eventProxy,
        'EventSummaryContainer_user_contacts'
      );
      const newEdge = ConnectionHandler.createEdge(
        store,
        newNode,
        newNode,
        'EventUserContactEdge'
      );
      newEdge.setValue(this.state.newContact.role, 'role');
      ConnectionHandler.insertEdgeAfter(conn, newEdge);
      // const emailConnection = ConnectionHandler.getConnection(
      //   eventProxy,
      //   'EventSummaryContainer_user_contact_emails'
      // );
      // const user_contact_email_edges = newEdge.getLinkedRecord('node').getLinkedRecord('user_contact_emails').getLinkedRecords('edges');
      // console.log('user_contact_email_edges', user_contact_email_edges);
      // user_contact_email_edges.map(email => {
      //   ConnectionHandler.insertEdgeAfter(emailConnection, email);
      //   return email;
      // })
    };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          // eslint-disable-next-line camelcase
          ...user_contact
        }
      },
      updater,
      onCompleted,
      onError
    });
  }

  deleteNewUserContactPhone(contactIdx, emailIdx) {
    const obj = _.cloneDeep(this.state);
    obj.newContact.node.user_contact_phones.edges.splice(emailIdx, 1);
    this.setState(obj);
  }

  deleteNewUserContactEmail(contactIdx, emailIdx) {
    const obj = _.cloneDeep(this.state);
    obj.newContact.node.user_contact_emails.edges.splice(emailIdx, 1);
    this.setState(obj);
  }

  deleteNewUserContactAddress(contactIdx, emailIdx) {
    const obj = _.cloneDeep(this.state);
    obj.newContact.node.user_contact_addresses.edges.splice(emailIdx, 1);
    this.setState(obj);
  }

  render() {
    const newContact = this.state.newContact;

    return (
      <div>
        {this.props.event.user_contacts.edges.length === 0 &&
          !this.state.editorOpen &&
          <div style={{ padding: '2em', textAlign: 'center' }}>
            <h4>This event has no contacts</h4>
            <br />
            <Button
              basic
              icon='plus'
              labelPosition='left'
              content='Add Contact'
              onClick={this.addContact}
            />
          </div>}
        {this.props.event.user_contacts.edges.map((contact, idx) =>
          <Dthc
            desktop={ContactEditDesktop}
            mobile={ContactEditDesktop}
            key={contact.node.id || idx}
            index={idx}
            contact={contact}
            localContact={this.state.localContacts[idx]}
            onChange={c => {
              this.contactChangeHandler(c, idx);
            }}
            commitUpdate={this.updateContact}
            delete={this.deleteContact}
            deleteUserContactEmail={this.deleteUserContactEmail}
            deleteUserContactPhone={this.deleteUserContactPhone}
            deleteUserContactAddress={this.deleteUserContactAddress}
            reset={this.resetContact}
            handleClickOutside
            avatarCache={this.state.avatarCache}
            updateAvatarCache={this.updateAvatarCache}
            iconStyle={{ display: 'none' }}
          >
            <ContactShow
              contact={contact}
              avatarCache={this.state.avatarCache}
              updateAvatarCache={this.updateAvatarCache}
            />
          </Dthc>
        )}
        {this.state.editorOpen &&
          <Dthc
            desktop={NewContactDesktop}
            mobile={NewContactDesktop}
            contact={newContact}
            localContact={newContact}
            onChange={c => {
              this.newContactChangeHandler(c);
            }}
            commitUpdate={this.createContact}
            initiallyOpen
            isNew
            avatarCache={this.state.avatarCache}
            updateAvatarCache={this.updateAvatarCache}
            onClickOutside
            deleteUserContactEmail={this.deleteNewUserContactEmail}
            deleteUserContactPhone={this.deleteNewUserContactPhone}
            deleteUserContactAddress={this.deleteNewUserContactAddress}
            reset={this.resetNewContact}
            viewer={this.props.viewer}
            event={this.props.event}
            refetchEvent={this.props.refetchEvent}
            relay={this.props.relay}
          >
            <div />
          </Dthc>}
        <br />
        {!this.state.editorOpen &&
          this.props.event.user_contacts.edges.length > 0 &&
          <a onClick={this.addContact}>+ Add Contact</a>}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Event/Show/Contacts.js